import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Pill } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { startGoogleLogin } from "@/components/AuthCallback";

export function destinationFor(user) {
  if (!user) return "/login";
  if (!user.role || !user.has_consent) return "/choose-role";
  if (user.role === "admin") return "/admin";
  if (user.role === "pharmacy") {
    if (user.needs_npi) return "/pharmacy/npi";
    return user.has_profile ? "/pharmacy" : "/pharmacy/onboarding";
  }
  if (user.needs_npi) return "/worker/npi";
  return user.has_profile ? "/worker" : "/worker/onboarding";
}

const AuthLayout = ({ title, subtitle, children }) => (
  <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
    <div className="w-full max-w-md">
      <Link to="/" className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-white">
          <Pill className="h-4 w-4" />
        </span>
        <span className="font-display text-lg font-semibold tracking-tight">FindRxStaff</span>
      </Link>
      <h1 className="mt-8 font-display text-3xl font-semibold tracking-tight">{title}</h1>
      <p className="mt-2 text-sm text-slate-600">{subtitle}</p>
      <div className="card-surface mt-7 p-6 sm:p-7">{children}</div>
    </div>
  </div>
);

export const GoogleButton = ({ label = "Continue with Google" }) => (
  <button type="button" onClick={startGoogleLogin} className="btn-ghost w-full" data-testid="google-auth-button">
    <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4" />
    {label}
  </button>
);

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await login(form.email, form.password);
      navigate(destinationFor(user));
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to manage your shifts and coverage.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <label className="field-label">Email</label>
          <input
            className="field-input"
            type="email"
            required
            data-testid="login-email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <div>
          <label className="field-label">Password</label>
          <input
            className="field-input"
            type="password"
            required
            data-testid="login-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </div>
        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="login-submit">
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <Link to="/forgot-password" className="block text-sm font-medium text-brand" data-testid="login-forgot-password">
          Forgot your password?
        </Link>
      </form>

      <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-widest text-slate-400">
        <span className="h-px flex-1 bg-slate-200" /> or <span className="h-px flex-1 bg-slate-200" />
      </div>
      <GoogleButton />

      <p className="mt-6 text-sm text-slate-600">
        New here?{" "}
        <Link to="/register" className="font-medium text-brand" data-testid="login-to-register">
          Create an account
        </Link>
      </p>
      <p className="mt-3 text-xs leading-relaxed text-slate-500" data-testid="login-legal-links">        By signing in you agree to our{" "}
        <Link to="/legal/terms" className="font-medium text-brand hover:underline">
          Terms of Use
        </Link>{" "}
        and{" "}
        <Link to="/legal/privacy" className="font-medium text-brand hover:underline">
          Privacy Policy
        </Link>
        . FindRxStaff is a connection platform — it does not employ, supervise or pay users, and does not
        process payments.
      </p>
      <div className="mt-5 rounded-lg bg-slate-50 p-3 text-xs leading-relaxed text-slate-500">
        <strong className="text-slate-700">Need help signing in?</strong>
        <br />
        Use the email you registered with, or reset your password above.
      </div>
    </AuthLayout>
  );
}

export { AuthLayout };
